import InsertChartOutlinedTwoToneIcon from '@mui/icons-material/InsertChartOutlinedTwoTone';
import PushPinIcon from '@mui/icons-material/PushPin';
import { DialogTitle } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  pinDashboard,
  IComposableDashboard,
  IComposableDashboardContext,
  updateDashboard,
  assignDashboard,
  findMultipleTeamsByIds,
  IRecruiterTeamNameAutoCompleteOption,
  IPinnedDashboard,
  unPinDashboard,
  deleteDashboard,
  getPinnedDashboardsCreatedByCurrentUser,
  getMyPinnedDashboards,
  getMyPinnedDashboardsCollection,
} from 'wisestep-api';
import { WsDialog } from 'wisestep-base';

import { ConfirmDeleteDialog, IconForDialog } from '../../../components';
import { useWorkspaceContext, useUserContext } from '../../../context';
import {
  ComposableAnalyticsDataAccessEnum,
  useComposableAnalyticsDataAccessHook,
} from '../../../hooks/useComposableAnalyticsDataAccessHook';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import ConfirmationDialog from '../../clientDetails/components/ConfirmationDialog';
import { StyledGrid } from '../../clientListingPage/components/ClientList';
import { formatTeams } from '../../jobs/helper';
import AnalyticsContentLayout from '../components/AnalyticsContentLayout';
import SaveDialog from '../components/dashboard/SaveDialog';
import ShareDashboardDialog from '../components/dashboard/ShareDashboardDialog';
import { defaultPinnedDashboard } from '../helper/getDefaultDashboardValues';
import { useComposableDashboardsContext } from '../hooks/useComposableDashboardsContext';
import { AnalyticsMenuItems, IAnalyticsMenuOption } from '../types';

import { jobAnalyticsOptions } from './AnalyticsSubOptions';

const CustomAnalyticsOptions = () => {
  const location = useLocation();
  const [options, setOptions] = useState<IAnalyticsMenuOption[]>([]);
  const [title, setTitle] = useState<string>('');
  const [openShareDialog, setOpenShareDialog] = useState<boolean>(false);
  const [openPinDialog, setOpenPinDialog] = useState<boolean>(false);
  const [pinnedDashboards, setPinnedDashboards] = useState<IPinnedDashboard[]>([]);
  const [selectedPinnedDashboard, setSelectedPinnedDashboard] =
    useState<IPinnedDashboard>(defaultPinnedDashboard);

  const {
    dashboards: customDashboards,
    setDashboards,
    setSelectedDashboardByIndex,
    selectedDashboard,
    setSelectedDashboard,
  } = useComposableDashboardsContext() as Required<IComposableDashboardContext>;
  const [isAssignFlow, setIsAssignFlow] = useState<boolean>(false);
  const [allowedTeams, setAllowedTeams] = useState<IRecruiterTeamNameAutoCompleteOption[]>([]);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState<boolean>(false);
  const workspace = useWorkspaceContext();
  const loggedInUser = useUserContext().userDTO;

  const { access: hasDataAccess, owns: isAdmin } = useComposableAnalyticsDataAccessHook([
    ComposableAnalyticsDataAccessEnum.SHARE_DASHBOARD,
    ComposableAnalyticsDataAccessEnum.PIN_DASHBOARD,
    ComposableAnalyticsDataAccessEnum.ASSIGN_DASHBOARD,
    ComposableAnalyticsDataAccessEnum.RENAME_DASHBOARD,
    ComposableAnalyticsDataAccessEnum.DELETE_DASHBOARD,
    ComposableAnalyticsDataAccessEnum.UNPIN_DASHBOARD,
  ]);

  const pinPromptText = `Are you sure want to Pin Dashboard "${
    selectedDashboard?.name ?? ''
  }" to Home page?`;

  const unPinPromptText = `Are you sure want to Unpin Dashboard "${
    selectedDashboard?.name ?? ''
  }" from Home page?`;

  const fetchPinnedDashboards = useCallback(async () => {
    const pinnedDashboardsData = await getPinnedDashboardsCreatedByCurrentUser();
    setPinnedDashboards(pinnedDashboardsData.data);
  }, []);

  const handlePinningDashboard = async (dashboardId: string) => {
    setOpenPinDialog(false);
    try {
      if (
        selectedPinnedDashboard?.id &&
        loggedInUser?.email &&
        selectedPinnedDashboard.users?.includes(loggedInUser.email) &&
        selectedDashboard.id
      ) {
        await unPinDashboard(selectedDashboard.id);
        setSelectedPinnedDashboard(defaultPinnedDashboard);
        setNotification(
          success(
            `Dashboard ${selectedDashboard?.name ?? ''} has been un-pinned from the Home page`
          )
        );
      } else {
        const responseDashboard = await getMyPinnedDashboards();
        const responseCollection = await getMyPinnedDashboardsCollection();
        const pinCount = [...responseDashboard.data, ...responseCollection.data];
        if (pinCount?.length < 8) {
          const response = await pinDashboard(dashboardId);
          setSelectedPinnedDashboard(response.data);
          setNotification(
            success(`Dashboard ${selectedDashboard?.name ?? ''} has been pinned to the Home page`)
          );
        } else {
          setNotification(
            failed(
              `Maximum 8 Dashboards are allowed to pin on the Home page. You can Unpin the current dashboards and add other Analytics dashboards`
            )
          );
        }
      }
      fetchPinnedDashboards();
    } catch (error) {
      setNotification(failed('Failed to pin the dashboard. Please try again!'));
    }
  };

  const updateDashboardStatus = useCallback(
    async (updateDashboardState: IComposableDashboard) => {
      if (!updateDashboardState) {
        setNotification(failed('Some error occurred. Please try again!!'));
        return;
      }
      try {
        updateDashboardState.charts =
          typeof updateDashboardState.charts === 'string'
            ? updateDashboardState.charts
            : JSON.stringify(updateDashboardState.charts);

        const response = isAssignFlow
          ? await assignDashboard(updateDashboardState)
          : await updateDashboard(updateDashboardState);

        const updatedDashboard = isAssignFlow ? response.data?.dashboard : response.data;
        setSelectedDashboard(response?.data.dashboard);
        setDashboards((prev) =>
          prev.map((dashboard) =>
            dashboard.id === updatedDashboard?.id ? updatedDashboard : dashboard
          )
        );
        if (isAssignFlow) {
          fetchPinnedDashboards();
          setSelectedPinnedDashboard(response?.data?.pinnedDashboard);
        }
        setNotification(
          success(`Dashboard has been ${isAssignFlow ? 'assigned' : 'shared'} successfully!`)
        );
      } catch (error) {
        setNotification(failed('Failed to update the dashboard. Please try again!'));
      }
      openShareDialog && setOpenShareDialog(false);
    },
    [
      fetchPinnedDashboards,
      isAssignFlow,
      openShareDialog,
      setDashboards,
      setNotification,
      setSelectedDashboard,
    ]
  );

  const onDeleteDashboard = async () => {
    try {
      await deleteDashboard(selectedDashboard.id ?? '');
      setSelectedDashboardByIndex(-1);
      setDashboards((prev) => prev?.filter(({ id }) => id !== selectedDashboard.id));
      setNotification(success(`Dashboard ${selectedDashboard?.name ?? ''} has been Deleted`));
    } catch {
      setNotification(failed('Failed to delete dashboard, Please try again !!'));
      setSelectedDashboardByIndex(-1);
    }
    setOpenDeleteDialog(false);
  };

  const onRenameDashboard = async (newDashboard: IComposableDashboard) => {
    try {
      const response = await updateDashboard({
        ...newDashboard,
        charts: JSON.stringify(newDashboard.charts),
      });
      setDashboards((prev) => {
        prev.splice(
          prev.findIndex((dashboard) => dashboard.id === newDashboard.id),
          1,
          response.data
        );
        return [...prev];
      });
      setNotification(success('Updated Dashboard Successfully!'));
    } catch (e) {
      setNotification(failed('Some error occurred. Please try later'));
    }
    setOpenUpdateDialog(false);
    setSelectedDashboardByIndex(-1);
  };

  const fetchTeamNames = useCallback(
    async (action: string, dashboard?: IComposableDashboard, pinDashboard?: IPinnedDashboard) => {
      try {
        const teamIds = action === 'share' ? dashboard?.sharedWithTeams : pinDashboard?.teams;
        if (teamIds && teamIds.length > 0) {
          const response = await findMultipleTeamsByIds(teamIds);
          setAllowedTeams(formatTeams(response.data));
        } else {
          setAllowedTeams([]);
        }
        return true;
      } catch (error) {
        return false;
      }
    },
    []
  );

  const getCustomDashboardForUserRole = useCallback(() => {
    const customDashboardForUserRole: IComposableDashboard[] = [];
    customDashboards.forEach((dashboard) => {
      if (dashboard.createdBy != 'Default') customDashboardForUserRole.push(dashboard);
    });
    return customDashboardForUserRole;
  }, [customDashboards]);

  const handleOnClick = useCallback(
    async (index: number, action: 'pin' | 'unpin' | 'assign' | 'share' | 'rename' | 'delete') => {
      setSelectedDashboardByIndex(index);
      const dashboard = customDashboards?.[index];
      const pinDashboard = pinnedDashboards?.find((pd) => pd.dashboard.id === dashboard?.id);
      setSelectedPinnedDashboard(pinDashboard?.id ? pinDashboard : defaultPinnedDashboard);
      if (!dashboard) {
        setNotification(
          failed('Dashboard has not been selected properly. Please try again later!')
        );
        return;
      }

      switch (action) {
        case 'pin':
        case 'unpin':
          setOpenPinDialog(true);
          break;
        case 'share': {
          const isTeamNamesFetched = await fetchTeamNames(action, dashboard);
          if (isTeamNamesFetched) {
            setIsAssignFlow(false);
            setOpenShareDialog(true);
          }
          break;
        }

        case 'assign': {
          const isTeamNamesFetched = await fetchTeamNames(action, dashboard, pinDashboard);
          if (isTeamNamesFetched) {
            setIsAssignFlow(true);
            setOpenShareDialog(true);
          }
          break;
        }
        case 'rename':
          setOpenUpdateDialog(true);
          break;
        case 'delete':
          setOpenDeleteDialog(true);
          break;

        default:
          break;
      }
    },
    [
      customDashboards,
      fetchTeamNames,
      pinnedDashboards,
      setNotification,
      setSelectedDashboardByIndex,
    ]
  );

  useEffect(() => {
    fetchPinnedDashboards();
  }, [fetchPinnedDashboards]);

  useEffect(() => {
    const pathnameSplit = location.pathname.split('/');
    const section = pathnameSplit[2];
    const isDashboardAssigned = (dashboard: IComposableDashboard) => {
      const listOfIds: string[] = loggedInUser?.belongsToTeams?.map((team) => team.id ?? '') ?? [];
      if (isDashboardCreator(dashboard)) {
        return `Created By: ${dashboard.createdByName}`;
      }
      if (isDashboardDefault(dashboard)) {
        return `Created By: ${dashboard.createdByName}`;
      }
      if (
        dashboard.sharedWithRecruiters?.includes(workspace?.userId ?? '') ||
        (workspace?.role && dashboard.sharedWithRoles?.includes(workspace?.role))
      ) {
        return `Assigned By: ${dashboard.createdByName}`;
      }
      if (dashboard.sharedWithTeams?.find((sharedTeam) => listOfIds.includes(sharedTeam))) {
        return `Assigned By: ${dashboard.createdByName}`;
      } else return 'Public Dashboard';
    };
    const isDashboardDefault = (dashboard: IComposableDashboard) =>
      dashboard.createdBy === 'Default';
    const isDashboardCreator = (dashboard: IComposableDashboard) =>
      dashboard.createdBy === workspace.userId || dashboard.createdBy === 'Default';
    const optionsForCreator = (dashboard: IComposableDashboard) =>
      isDashboardCreator(dashboard)
        ? [
            hasDataAccess[0] || isAdmin
              ? {
                  text: 'Share Dashboard',
                  onClick: (index) => handleOnClick(index, 'share'),
                }
              : null,
            hasDataAccess[2] || isAdmin
              ? {
                  text: 'Assign Dashboard',
                  onClick: (index) => handleOnClick(index, 'assign'),
                }
              : null,
            hasDataAccess[3] || isAdmin
              ? {
                  text: 'Rename Dashboard',
                  onClick: (index) => handleOnClick(index, 'rename'),
                }
              : null,
            hasDataAccess[4] || isAdmin
              ? {
                  text: 'Delete Dashboard',
                  onClick: (index) => handleOnClick(index, 'delete'),
                }
              : null,
          ].filter(Boolean)
        : [];

    const options =
      section === AnalyticsMenuItems.YOUR_DASHBOARDS
        ? (isAdmin ? customDashboards : getCustomDashboardForUserRole())?.map(
            (dashboard, index) => {
              const isPinned = pinnedDashboards?.find(
                (pd) =>
                  pd.dashboard.id === dashboard.id &&
                  loggedInUser?.email &&
                  pd.users?.includes(loggedInUser.email)
              );
              return {
                id: dashboard.id,
                label: dashboard.name,
                createdByName: dashboard.createdByName,
                icon: <InsertChartOutlinedTwoToneIcon />,
                url: '/workspace-users-and-teams/your-dashboards/view',
                caption: isDashboardAssigned(dashboard),
                description: dashboard.description,
                createdOn: dashboard.createdOn,
                modifiedOn: dashboard.modifiedOn,
                showKebabMenu: true,
                kebabMenuOptions: [
                  (hasDataAccess[5] || isAdmin) && isPinned
                    ? {
                        text: 'Unpin from Home Page',
                        onClick: (index) => handleOnClick(index, 'unpin'),
                      }
                    : null,
                  (hasDataAccess[1] || isAdmin) && !isPinned
                    ? {
                        text: 'Pin To Home Page',
                        onClick: (index) => handleOnClick(index, 'pin'),
                      }
                    : null,
                  ...optionsForCreator(dashboard),
                ].filter(Boolean),
                state: {
                  index,
                },
              };
            }
          )
        : jobAnalyticsOptions;

    setOptions(options);
    setTitle(section === AnalyticsMenuItems.YOUR_DASHBOARDS ? 'Your Dashboards' : 'Jobs Analytics');
  }, [
    customDashboards,
    getCustomDashboardForUserRole,
    handleOnClick,
    isAdmin,
    location.pathname,
    loggedInUser,
    pinnedDashboards,
    workspace,
  ]);

  const isDashboardPinned =
    selectedPinnedDashboard?.id &&
    loggedInUser?.email &&
    selectedPinnedDashboard.users?.includes(loggedInUser.email);

  return (
    <StyledGrid item xs={12}>
      <AnalyticsContentLayout
        key="my_dashboard"
        options={options}
        title={title}
        selectedDashboardId="dashboard"
      />
      {openPinDialog && (
        <WsDialog openDialog={openPinDialog}>
          <DialogTitle>
            <IconForDialog icon={<PushPinIcon />} />
          </DialogTitle>
          <ConfirmationDialog
            prompt={isDashboardPinned ? unPinPromptText : pinPromptText}
            onConfirm={() => handlePinningDashboard(selectedDashboard?.id ?? '')}
            onSkip={() => {
              setSelectedPinnedDashboard(defaultPinnedDashboard);
              setOpenPinDialog(false);
            }}
            confirmText={isDashboardPinned ? 'Unpin' : 'Pin'}
            skipText="Cancel"
          />
        </WsDialog>
      )}
      {openShareDialog && (
        <ShareDashboardDialog
          dashboard={selectedDashboard}
          onCloseDialog={() => setOpenShareDialog(false)}
          openShareDialog={openShareDialog}
          onSave={(dashboard) => updateDashboardStatus(dashboard as IComposableDashboard)}
          isAssignFlow={isAssignFlow}
          sharedToTeams={allowedTeams}
          selectedPinnedDashboard={selectedPinnedDashboard}
        />
      )}
      {openDeleteDialog && (
        <ConfirmDeleteDialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          onConfirm={onDeleteDashboard}
          deletingItem={selectedDashboard.name}
        />
      )}
      {openUpdateDialog && (
        <SaveDialog
          openSaveModal={openUpdateDialog}
          setSaveModal={setOpenUpdateDialog}
          saveDashboard={onRenameDashboard}
          update={true}
          customUpdateText="Update Dashboard Name"
        />
      )}
    </StyledGrid>
  );
};

export default CustomAnalyticsOptions;
